import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import './Observability.css';



const COLORS = {
  haiku: '#9d6eff',
  sonnet: '#c9a227',
  success: '#2ea043',
  error: '#ef4444',
  primary: '#c9a227',
  secondary: '#1e90ff',
};

function ParticleField() {
  const canvasRef = React.useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles = Array.from({ length: 30 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.2,
      vx: (Math.random() - 0.5) * 0.2,
      vy: (Math.random() - 0.5) * 0.2,
    }));
    let raf;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(201, 162, 39, 0.4)';
        ctx.fill();
      });
      raf = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={canvasRef} className="obs-particles" />;
}

export default function Observability() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState('calls'); // 'calls' | 'waitlist'

  useEffect(() => {
    async function checkAccessAndFetch() {
      try {
        // Step 1: Verify user is logged in
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          window.location.href = '/login';
          return;
        }

        // Step 2: Check admin role
        const { data: roles, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id);

        if (roleError) throw roleError;
        const isAdmin = roles?.some(r => r.role === 'admin' || r.role === 'superadmin');

        if (!isAdmin) {
          // Log unauthorized attempt
          await supabase.from('admin_audit_log').insert({
            user_id: user.id,
            user_email: user.email,
            action: 'UNAUTHORIZED_ACCESS_ATTEMPT',
            resource: '/admin/observability',
            user_agent: navigator.userAgent,
          });
          window.location.href = '/';
          return;
        }

        setAuthorized(true);

        // Step 3: Log admin access
        await supabase.from('admin_audit_log').insert({
          user_id: user.id,
          user_email: user.email,
          action: 'VIEW_OBSERVABILITY',
          resource: '/admin/observability',
          user_agent: navigator.userAgent,
        });

        // Step 4: Fetch the calls data
        const { data, error: fetchError } = await supabase
          .from('llm_calls')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(500);

        if (fetchError) throw fetchError;
        setCalls(data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    checkAccessAndFetch();
  }, []);

  // While checking auth, show loading
  if (loading && !authorized) {
    return (
      <div className="obs-page">
        <div className="obs-loading">Verifying access...</div>
      </div>
    );
  }

  // Calculate stats
  const totalCalls = calls.length;
  const totalCost = calls.reduce((sum, c) => sum + parseFloat(c.cost_usd || 0), 0);
  const avgLatency = calls.length > 0
    ? Math.round(calls.reduce((sum, c) => sum + (c.latency_ms || 0), 0) / calls.length)
    : 0;
  const successRate = calls.length > 0
    ? ((calls.filter(c => c.status === 'success').length / calls.length) * 100).toFixed(1)
    : 0;

  // Calls grouped by hour for time chart
  const callsByHour = {};
  calls.forEach(c => {
    const hour = new Date(c.created_at).toISOString().slice(0, 13);
    callsByHour[hour] = (callsByHour[hour] || 0) + 1;
  });
  const timeData = Object.entries(callsByHour)
    .sort()
    .slice(-24)
    .map(([hour, count]) => ({
      time: new Date(hour + ':00:00').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      calls: count,
    }));

  // Cost by model
  const costByModel = {};
  calls.forEach(c => {
    const m = c.prompt_type || 'unknown';
    costByModel[m] = (costByModel[m] || 0) + parseFloat(c.cost_usd || 0);
  });
  const modelData = Object.entries(costByModel).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value: parseFloat(value.toFixed(6)),
  }));

  // Latency by model
  const latencyByModel = {};
  const latencyCount = {};
  calls.forEach(c => {
    const m = c.prompt_type || 'unknown';
    latencyByModel[m] = (latencyByModel[m] || 0) + (c.latency_ms || 0);
    latencyCount[m] = (latencyCount[m] || 0) + 1;
  });
  const latencyData = Object.entries(latencyByModel).map(([name, total]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    latency: Math.round(total / latencyCount[name]),
  }));

  return (
    <div className="obs-page">
      <ParticleField />
      
      <nav className="obs-nav">
        <a href="/" className="obs-nav__back">← Curx AI</a>
        <h1 className="obs-nav__title">
          <span className="obs-nav__dot"></span>
          OBSERVABILITY
        </h1>
        <span className="obs-nav__badge">
          {activeTab === 'calls' ? `${totalCalls} calls` : 'WAITLIST'}
        </span>
      </nav>

      <div className="obs-tabs">
        <button
          type="button"
          className={'obs-tab' + (activeTab === 'calls' ? ' obs-tab--active' : '')}
          onClick={() => setActiveTab('calls')}
        >
          LLM Calls
        </button>
        <button
          type="button"
          className={'obs-tab' + (activeTab === 'waitlist' ? ' obs-tab--active' : '')}
          onClick={() => setActiveTab('waitlist')}
        >
          Waitlist
        </button>
      </div>

      {activeTab === 'waitlist' && <WaitlistTab />}

      {activeTab === 'calls' && loading && (
        <div className="obs-loading">Loading observability data...</div>
      )}
      {activeTab === 'calls' && error && (
        <div className="obs-error">Error: {error}</div>
      )}
      {activeTab === 'calls' && !loading && !error && (
      <div className="obs-content">
        <div className="obs-header">
          <p className="obs-eyebrow">LLM TELEMETRY · LIVE</p>
          <h2 className="obs-title">Production <em>Insights</em></h2>
          <p className="obs-sub">Every call. Every token. Every cent.</p>
        </div>

        {/* Top Stats */}
        <div className="obs-stats">
          <div className="obs-stat">
            <div className="obs-stat__label">Total Calls</div>
            <div className="obs-stat__value">{totalCalls.toLocaleString()}</div>
            <div className="obs-stat__sub">All time</div>
          </div>
          <div className="obs-stat">
            <div className="obs-stat__label">Total Cost</div>
            <div className="obs-stat__value">${totalCost.toFixed(4)}</div>
            <div className="obs-stat__sub">USD spent</div>
          </div>
          <div className="obs-stat">
            <div className="obs-stat__label">Avg Latency</div>
            <div className="obs-stat__value">{avgLatency}<span className="obs-stat__unit">ms</span></div>
            <div className="obs-stat__sub">Across all calls</div>
          </div>
          <div className="obs-stat">
            <div className="obs-stat__label">Success Rate</div>
            <div className="obs-stat__value">{successRate}<span className="obs-stat__unit">%</span></div>
            <div className="obs-stat__sub">Last 500 calls</div>
          </div>
        </div>

        {/* Charts */}
        <div className="obs-charts">
          <div className="obs-chart obs-chart--wide">
            <h3 className="obs-chart__title">Calls Over Time</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={timeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: '#0a0e1a',
                    border: '1px solid #c9a227',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="calls"
                  stroke={COLORS.primary}
                  strokeWidth={2}
                  dot={{ fill: COLORS.primary, r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="obs-chart">
            <h3 className="obs-chart__title">Cost by Model</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={modelData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  label={(entry) => `${entry.name}: $${entry.value.toFixed(4)}`}
                  labelLine={false}
                >
                  {modelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.name === 'Haiku' ? COLORS.haiku : COLORS.sonnet} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: '#0a0e1a',
                    border: '1px solid #c9a227',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="obs-chart">
            <h3 className="obs-chart__title">Average Latency by Model</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={latencyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: '#0a0e1a',
                    border: '1px solid #c9a227',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="latency" fill={COLORS.secondary} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Calls Table */}
        <div className="obs-table-section">
          <h3 className="obs-chart__title">Recent Calls</h3>
          <div className="obs-table-wrapper">
            <table className="obs-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Model</th>
                  <th>In Tokens</th>
                  <th>Out Tokens</th>
                  <th>Latency</th>
                  <th>Cost</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {calls.slice(0, 20).map((c) => (
                  <tr key={c.id}>
                    <td>{new Date(c.created_at).toLocaleTimeString()}</td>
                    <td>
                      <span className={`obs-pill obs-pill--${c.prompt_type}`}>
                        {c.prompt_type}
                      </span>
                    </td>
                    <td className="obs-table__model">{c.model}</td>
                    <td>{(c.input_tokens || 0).toLocaleString()}</td>
                    <td>{(c.output_tokens || 0).toLocaleString()}</td>
                    <td>{c.latency_ms}ms</td>
                    <td>${parseFloat(c.cost_usd || 0).toFixed(6)}</td>
                    <td>
                      <span className={`obs-status obs-status--${c.status}`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

/* ============================================================
   WAITLIST TAB
   ============================================================ */

function WaitlistTab() {
  const [stats, setStats] = useState({ total: 0, recent24h: 0 });
  const [signups, setSignups] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const [byRole, setByRole] = useState([]);
  const [byDay, setByDay] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Pull recent signups via service-role-bypassing RLS isn't available client-side;
        // however since the Observability page already gates on admin role, we use the same
        // supabase client. Admin users may not have direct SELECT on waitlist_signups
        // (only INSERT is granted publicly). We expose admin-only reads via a SECURITY
        // DEFINER RPC if needed; for V1 we display aggregate counts via the public RPCs
        // and best-effort detail reads.
        const [{ data: totalData }, { data: recentData }] = await Promise.all([
          supabase.rpc('get_waitlist_count'),
          supabase.rpc('get_waitlist_count_recent'),
        ]);
        // Best-effort: try to read recent signups (will return [] if RLS denies)
        const { data: rows } = await supabase
          .from('waitlist_signups')
          .select('id, email, name, role, referral_code, referrals_count, referred_by, created_at')
          .order('created_at', { ascending: false })
          .limit(200);

        if (cancelled) return;
        setStats({
          total: Number(totalData) || 0,
          recent24h: Number(recentData) || 0,
        });

        const list = rows || [];
        setSignups(list.slice(0, 20));

        // Aggregate top referrers
        const refCounts = {};
        for (const r of list) {
          if ((r.referrals_count || 0) > 0) {
            refCounts[r.id] = { name: r.name, email: r.email, count: r.referrals_count };
          }
        }
        const topRef = Object.values(refCounts)
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
        setTopReferrers(topRef);

        // By role
        const roleMap = {};
        for (const r of list) {
          const rk = r.role || 'Unknown';
          roleMap[rk] = (roleMap[rk] || 0) + 1;
        }
        setByRole(Object.entries(roleMap).map(([role, count]) => ({ role, count })));

        // By day (last 14 days)
        const dayMap = {};
        const today = new Date();
        for (let i = 13; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          const key = d.toISOString().slice(0, 10);
          dayMap[key] = 0;
        }
        for (const r of list) {
          const key = r.created_at?.slice(0, 10);
          if (key in dayMap) dayMap[key]++;
        }
        setByDay(Object.entries(dayMap).map(([day, count]) => ({
          day: day.slice(5),
          count,
        })));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load waitlist data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="obs-loading">Loading waitlist data...</div>;
  if (error)   return <div className="obs-error">Error: {error}</div>;

  const referredCount = signups.filter(s => s.referred_by).length;
  const organicCount = signups.length - referredCount;

  return (
    <div className="obs-content">
      <div className="obs-header">
        <p className="obs-eyebrow">WAITLIST · LIVE</p>
        <h2 className="obs-title">Pre-launch <em>Signal</em></h2>
        <p className="obs-sub">Who's waiting. Where they came from. What they want.</p>
      </div>

      <div className="obs-stats">
        <div className="obs-stat">
          <div className="obs-stat__label">Total Signups</div>
          <div className="obs-stat__value">{stats.total.toLocaleString()}</div>
          <div className="obs-stat__sub">All time</div>
        </div>
        <div className="obs-stat">
          <div className="obs-stat__label">Last 24h</div>
          <div className="obs-stat__value">{stats.recent24h.toLocaleString()}</div>
          <div className="obs-stat__sub">Recent signups</div>
        </div>
        <div className="obs-stat">
          <div className="obs-stat__label">Referred</div>
          <div className="obs-stat__value">{referredCount}<span className="obs-stat__unit"> / {signups.length}</span></div>
          <div className="obs-stat__sub">In recent 200</div>
        </div>
        <div className="obs-stat">
          <div className="obs-stat__label">Organic</div>
          <div className="obs-stat__value">{organicCount}<span className="obs-stat__unit"> / {signups.length}</span></div>
          <div className="obs-stat__sub">In recent 200</div>
        </div>
      </div>

      <div className="obs-charts">
        <div className="obs-chart obs-chart--wide">
          <h3 className="obs-chart__title">Signups Over Last 14 Days</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={byDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="day" stroke="#9ca3af" fontSize={11} />
              <YAxis stroke="#9ca3af" fontSize={11} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: '#0a0e1a',
                  border: '1px solid #c9a227',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="count" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="obs-chart">
          <h3 className="obs-chart__title">By Role (recent 200)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={byRole}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                dataKey="count"
                label={(entry) => `${entry.role}: ${entry.count}`}
                labelLine={false}
              >
                {byRole.map((_, i) => (
                  <Cell key={`role-${i}`} fill={ROLE_COLORS[i % ROLE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: '#0a0e1a',
                  border: '1px solid #c9a227',
                  borderRadius: '8px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="obs-charts">
        <div className="obs-chart obs-chart--wide">
          <h3 className="obs-chart__title">Top Referrers</h3>
          {topReferrers.length === 0 ? (
            <p style={{ color: '#9ca3af', fontStyle: 'italic', padding: '12px 0' }}>
              No referrals tracked yet.
            </p>
          ) : (
            <table className="obs-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Referrals</th>
                </tr>
              </thead>
              <tbody>
                {topReferrers.map((r, i) => (
                  <tr key={r.email}>
                    <td>#{i + 1}</td>
                    <td>{r.name}</td>
                    <td className="obs-table__model">{r.email}</td>
                    <td>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="obs-chart obs-chart--wide">
          <h3 className="obs-chart__title">Recent Signups (last 20)</h3>
          {signups.length === 0 ? (
            <p style={{ color: '#9ca3af', fontStyle: 'italic', padding: '12px 0' }}>
              No signup details accessible from this client. (Service-role required for full read.)
            </p>
          ) : (
            <table className="obs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Source</th>
                  <th>Referrals</th>
                </tr>
              </thead>
              <tbody>
                {signups.map((s) => (
                  <tr key={s.id}>
                    <td>{new Date(s.created_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}</td>
                    <td>{s.name}</td>
                    <td>
                      <span className="obs-pill obs-pill--haiku">{s.role}</span>
                    </td>
                    <td>
                      <span className={'obs-status obs-status--' + (s.referred_by ? 'success' : 'error')}>
                        {s.referred_by ? 'referred' : 'organic'}
                      </span>
                    </td>
                    <td>{s.referrals_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const ROLE_COLORS = ['#c9a227', '#9d6eff', '#1e90ff', '#2ea043', '#ff7466', '#00d9ff', '#d4af37'];